import L from 'leaflet';
import 'leaflet.markercluster';
import PlaceDataUtils, { PlaceDefinition } from '../../utils/PlaceDataUtils';
import { PlaceMarker } from './PlaceMarker';
import './PlaceMarkerCluster.css';

// Define the structure for category count
interface CategoryCount {
    category: string;
    count: number;
    iconPath: string;
}

/**
 * Custom marker cluster group for place markers
 * Creates cluster icons showing category distribution
 */
export class PlaceMarkerClusterGroup {
    private clusterGroup: L.MarkerClusterGroup;
    private onClusterClickCallback?: () => void;

    constructor(onClusterClick?: () => void) {
        this.onClusterClickCallback = onClusterClick;
        this.clusterGroup = L.markerClusterGroup({
            maxClusterRadius: 80,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            iconCreateFunction: (cluster) => this.createClusterIcon(cluster),
            // Disable default animations for better performance
            animate: true,
            animateAddingMarkers: false,
        });

        // Listen to cluster click events to deactivate location tracking
        if (this.onClusterClickCallback) {
            this.clusterGroup.on('clusterclick', () => {
                this.onClusterClickCallback?.();
            });
        }
    }

    /**
     * Creates a custom cluster icon showing category icons and count
     */
    private createClusterIcon(cluster: L.MarkerCluster): L.DivIcon {
        const markers = cluster.getAllChildMarkers();
        const totalCount = markers.length;

        // Get place definitions from markers
        const places: PlaceDefinition[] = markers
            .filter(marker => marker instanceof PlaceMarker)
            .map(marker => (marker as PlaceMarker).getPlaceDefinition());

        // Count categories
        const categoryMap = new Map<string, number>();
        places.forEach(place => {
            const category = place.placeCategories[0] || 'OTHER';
            categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
        });

        // Sort by count and get all categories
        const sortedCategories: CategoryCount[] = Array.from(categoryMap.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([category, count]) => ({
                category,
                count,
                iconPath: PlaceDataUtils.getPlaceCategoryDetail(category)?.iconPath || '/icons/places/Tree-16.svg'
            }));

        // Create HTML for cluster icon
        const clusterHtml = this.createClusterHtml(totalCount, sortedCategories);

        return L.divIcon({
            html: clusterHtml,
            className: 'place-marker-cluster',
            iconSize: L.point(50, 50),
            iconAnchor: [25, 25],
        });
    }

    /**
     * Creates the HTML content for cluster icon
     */
    private createClusterHtml(totalCount: number, categories: CategoryCount[]): string {
        let html = '<div class="place-cluster-container">';
        html += '<div class="place-cluster-circle">';
        html += `<div class="place-cluster-count">${totalCount}</div>`;
        html += '</div>';
        
        if (categories.length > 0) {
            html += '<div class="place-cluster-categories">';
            categories.forEach((cat) => {
                html += `<img src="${cat.iconPath}" alt="category" class="place-cluster-category-icon" />`;
            });
            html += '</div>';
        }
        
        html += '</div>';
        
        return html;
    }

    /**
     * Adds the cluster group to the map
     */
    public addTo(map: L.Map): void {
        this.clusterGroup.addTo(map);
    }

    /**
     * Adds a marker to the cluster group
     */
    public addLayer(marker: PlaceMarker): void {
        this.clusterGroup.addLayer(marker);
    }

    /**
     * Removes a marker from the cluster group
     */
    public removeLayer(marker: PlaceMarker): void {
        this.clusterGroup.removeLayer(marker);
    }

    /**
     * Gets all layers (markers) in the cluster group
     */
    public getLayers(): L.Layer[] {
        return this.clusterGroup.getLayers();
    }

    /**
     * Gets all place markers (excluding cluster markers) from the cluster group
     */
    public getAllPlaceMarkers(): PlaceMarker[] {
        const allLayers = this.clusterGroup.getLayers();
        return allLayers.filter(layer => layer instanceof PlaceMarker) as PlaceMarker[];
    }

    /**
     * Checks if a marker with the given place ID exists in the cluster
     */
    public hasMarkerWithPlaceId(placeId: string): boolean {
        const markers = this.getAllPlaceMarkers();
        return markers.some(marker => marker.getPlaceId() === placeId);
    }

    /**
     * Clears all markers from the cluster group
     */
    public clearLayers(): void {
        this.clusterGroup.clearLayers();
    }

    /**
     * Removes the cluster group from the map
     */
    public remove(): void {
        this.clusterGroup.remove();
    }

    /**
     * Gets the underlying Leaflet MarkerClusterGroup
     */
    public getClusterGroup(): L.MarkerClusterGroup {
        return this.clusterGroup;
    }
}
